import type { EventFilter, RealtimeFrame, SubscribeCallback } from "./types";

export type ChannelHost = {
  acquire(topic: string, channel: RealtimeChannel): Promise<void>;
  release(topic: string, channel: RealtimeChannel): Promise<void>;
};

type Binding = {
  event: string;
  callback: (payload: unknown) => void;
};

/** `*` matches every event; `segment:*` every event starting with `segment:`; anything else matches exactly. */
const matchesEvent = (pattern: string, event: string): boolean => {
  if (pattern === "*") return true;
  if (pattern.endsWith(":*")) return event.startsWith(pattern.slice(0, -1));
  return pattern === event;
};

type ChannelState ="idle" | "subscribing" | "subscribed" | "closed";

export class RealtimeChannel {
  private readonly bindings: Binding[] = [];
  private state: ChannelState = "idle";
  private statusCallback?: SubscribeCallback;

  constructor(
    readonly topic: string,
    private readonly host: ChannelHost,
  ) {}

  on<T = unknown>(filter: EventFilter, callback: (payload: T) => void): this {
    if (this.state === "closed") return this;
    this.bindings.push({ event: filter.event, callback: callback as (payload: unknown) => void });
    return this;
  }

  subscribe(callback?: SubscribeCallback): this {
    if (this.state === "closed") return this;
    this.statusCallback = callback;

    if (this.state === "subscribing" || this.state === "subscribed") {
      if (this.state === "subscribed") callback?.("SUBSCRIBED");
      return this;
    }

    this.state = "subscribing";
    this.host
      .acquire(this.topic, this)
      .then(() => {
        if (this.state === "closed") return;
        this.state = "subscribed";
        callback?.("SUBSCRIBED");
      })
      .catch((err: unknown) => {
        if (this.state === "closed") return;
        this.state = "idle";
        callback?.("CHANNEL_ERROR", err instanceof Error ? err : new Error(String(err)));
      });

    return this;
  }

  async remove(): Promise<"ok" | "error"> {
    if (this.state === "closed") return "ok";

    const wasActive = this.isActive;
    this.state = "closed";

    try {
      if (wasActive) await this.host.release(this.topic, this);
      this.statusCallback?.("CLOSED");
      return "ok";
    } catch {
      return "error";
    }
  }

  /** @internal */
  dispatch(frame: RealtimeFrame): void {
    if (this.state === "closed") return;
    const event = frame.event?.toLowerCase() ?? "";
    for (const binding of this.bindings) {
      if (matchesEvent(binding.event.toLowerCase(), event)) {
        binding.callback(frame.payload);
      }
    }
  }

  /** @internal */
  notifyReconnectError(error: Error): void {
    if (this.state === "closed") return;
    this.statusCallback?.("CHANNEL_ERROR", error);
  }

  /** @internal */
  notifyResubscribed(): void {
    if (this.state === "closed") return;
    this.state = "subscribed";
    this.statusCallback?.("SUBSCRIBED");
  }

  /** @internal */
  get isActive(): boolean {
    return this.state === "subscribed" || this.state === "subscribing";
  }
}
